/* XCup marketing sections — shared behaviours, 1:1 ports of the
   shopify-islands XCup* components (V1, the live set):
   - FadeIn: IntersectionObserver threshold 0.2 -> .is-visible (CSS 1s ease)
   - video panels: click-to-play overlay + mute/play controls (Problem, USPs)
   - testimonials: arrow scroll (+-400px smooth)
   - FAQ: single-open accordion, first item open
   - hero: mouse spotlight + content parallax tilt (desktop pointer only)
   - sticky bar: visible past 80% viewport until the buy box is on screen
   - scroll-to: [data-xc-scroll] buttons; "product" falls back to the s1-pdp
     element so the landing page needs no manual anchor ids */

const xcScrollTo = (target) => {
  const el = document.getElementById(target)
    || (target === 'product' && document.querySelector('s1-pdp'))
    || null;
  if (!el) return;
  /* the buy box lands on its layout (past the section's top padding);
     the theme's html scroll-padding-top keeps it under the sticky header */
  const anchor = (target === 'product' && el.querySelector('.s1-pdp__layout')) || el;
  anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

/* one shared observer for every fade-in block on the page */
let fadeIo = null;
const observeFades = (root) => {
  if (!fadeIo) {
    fadeIo = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) {
          en.target.classList.add('is-visible');
          fadeIo.unobserve(en.target);
        }
      });
    }, { threshold: 0.2 });
  }
  root.querySelectorAll('.s1-xc-fade:not(.is-visible)').forEach((el) => fadeIo.observe(el));
};

class S1XcupSection extends HTMLElement {
  connectedCallback() {
    this._abort = new AbortController();
    const { signal } = this._abort;
    observeFades(this);

    /* scroll buttons */
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-xc-scroll]');
      if (btn) xcScrollTo(btn.dataset.xcScroll);
    }, { signal });

    /* video panels (Problem / USP sections): overlay click toggles play,
       corner buttons control mute + play; overlay/caption fade while playing.
       Markup ships preload=metadata (first frame only — the raw files are
       24-38MB); nearing the viewport warms preload to auto so tapping play
       feels instant without the page-load download. */
    if (!this._videoWarmIo) {
      this._videoWarmIo = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          const v = en.target.querySelector('video');
          if (v && v.paused && v.preload !== 'auto') v.preload = 'auto';
          this._videoWarmIo.unobserve(en.target);
        });
      }, { rootMargin: '200px' });
    }
    this.querySelectorAll('[data-xc-video-panel]').forEach((panel) => {
      this._videoWarmIo.observe(panel);
    });

    this.querySelectorAll('[data-xc-video-panel]').forEach((panel) => {
      const video = panel.querySelector('video');
      if (!video) return;
      const sync = () => panel.classList.toggle('is-playing', !video.paused);
      const togglePlay = () => {
        if (video.paused) video.play().catch(() => {});
        else video.pause();
      };
      video.addEventListener('play', sync, { signal });
      video.addEventListener('pause', sync, { signal });
      video.addEventListener('click', togglePlay, { signal });
      const overlay = panel.querySelector('[data-xc-video-overlay]');
      if (overlay) overlay.addEventListener('click', togglePlay, { signal });
      const playBtn = panel.querySelector('[data-xc-video-play]');
      if (playBtn) playBtn.addEventListener('click', (e) => { e.stopPropagation(); togglePlay(); }, { signal });
      const muteBtn = panel.querySelector('[data-xc-video-mute]');
      if (muteBtn) {
        muteBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          video.muted = !video.muted;
          panel.classList.toggle('is-muted', video.muted);
        }, { signal });
        panel.classList.toggle('is-muted', video.muted);
      }
    });

    /* marquee: pause the 60s loop while the band is off-screen (battery /
       main-thread savings, no visual change — it scrolls infinitely anyway) */
    const band = this.querySelector('.s1-xc-audience__band');
    if (band) {
      this._marqueeIo = new IntersectionObserver((entries) => {
        entries.forEach((en) => band.classList.toggle('is-paused', !en.isIntersecting));
      });
      this._marqueeIo.observe(band);
    }

    /* testimonials arrows */
    const strip = this.querySelector('[data-xc-testimonials]');
    if (strip) {
      const prev = this.querySelector('[data-xc-t-prev]');
      const next = this.querySelector('[data-xc-t-next]');
      if (prev) prev.addEventListener('click', () => strip.scrollBy({ left: -400, behavior: 'smooth' }), { signal });
      if (next) next.addEventListener('click', () => strip.scrollBy({ left: 400, behavior: 'smooth' }), { signal });
    }

    /* FAQ accordion: single open, toggling the open item closes it */
    this.querySelectorAll('[data-xc-faq-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const item = btn.closest('[data-xc-faq-item]');
        const wasOpen = item.classList.contains('is-open');
        this.querySelectorAll('[data-xc-faq-item]').forEach((it) => {
          it.classList.remove('is-open');
          const b = it.querySelector('[data-xc-faq-toggle]');
          if (b) b.setAttribute('aria-expanded', 'false');
        });
        if (!wasOpen) {
          item.classList.add('is-open');
          btn.setAttribute('aria-expanded', 'true');
        }
      }, { signal });
    });
  }

  disconnectedCallback() {
    if (this._abort) this._abort.abort();
    if (this._videoWarmIo) { this._videoWarmIo.disconnect(); this._videoWarmIo = null; }
    if (this._marqueeIo) { this._marqueeIo.disconnect(); this._marqueeIo = null; }
  }
}

class S1XcupHero extends S1XcupSection {
  connectedCallback() {
    super.connectedCallback();
    const { signal } = this._abort;
    const spotlight = this.querySelector('[data-xc-spotlight]');
    const content = this.querySelector('[data-xc-hero-content]');
    /* SPA listens on window mousemove; fine pointers only so touch scroll
       never fights the transform. Writes are rAF-batched — mousemove can
       fire far more often than frames paint, and two style writes per event
       force layout thrash the SPA's setState also suffered from. */
    if (window.matchMedia('(pointer: fine)').matches) {
      let pending = null;
      const apply = () => {
        const e = pending;
        pending = null;
        const rect = this.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        if (spotlight) spotlight.style.transform = `translate(${x * 50}px, ${y * 50}px)`;
        if (content) content.style.transform = `perspective(1000px) rotateX(${y * 2}deg) rotateY(${x * 2}deg)`;
      };
      window.addEventListener('mousemove', (e) => {
        if (!pending) requestAnimationFrame(apply);
        pending = e;
      }, { signal });
    }
  }
}

class S1XcupSticky extends S1XcupSection {
  connectedCallback() {
    super.connectedCallback();
    const { signal } = this._abort;
    const bar = this.querySelector('[data-xc-sticky]');
    if (!bar) return;
    const onScroll = () => {
      const productSection = document.getElementById('product') || document.querySelector('s1-pdp');
      const vh = window.innerHeight;
      let visible;
      if (productSection) {
        const rect = productSection.getBoundingClientRect();
        const isPastHero = window.scrollY > vh * 0.8;
        const isBeforeProduct = rect.top > vh - 100;
        const isPastProduct = rect.bottom < 0;
        visible = isPastHero && (isBeforeProduct || isPastProduct);
      } else {
        visible = window.scrollY > vh;
      }
      bar.classList.toggle('is-visible', visible);
    };
    window.addEventListener('scroll', onScroll, { signal, passive: true });
    onScroll();
  }
}

if (!customElements.get('s1-xcup-section')) customElements.define('s1-xcup-section', S1XcupSection);
if (!customElements.get('s1-xcup-hero')) customElements.define('s1-xcup-hero', S1XcupHero);
if (!customElements.get('s1-xcup-sticky')) customElements.define('s1-xcup-sticky', S1XcupSticky);
